import { T, STATUS_COLOR } from './ui';
import type { AgentState, Alert } from '../api/client';

interface NavItem { id: string; icon: string; label: string; badge?: number; dot?: string; }

const NAV_ITEMS: NavItem[] = [
 { id: 'dashboard', icon: '◈', label: 'Dashboard' },
 { id: 'alerts', icon: '🔔', label: 'Alert Center' },
 { id: 'products', icon: '◻', label: 'Catalog' },
 { id: 'provenance', icon: '◎', label: 'Provenance' },
 { id: 'agents', icon: '⬡', label: 'Agents' },
 { id: 'procurement', icon: '🤝', label: 'Procurement' },
 { id: 'retailers', icon: '🏭', label: 'Retailers' },
 { id: 'apikeys', icon: '🔑', label: 'API Keys' },
 { id: 'control', icon: '◉', label: 'Control Plane' },
 // ── v6 ────────────────────────────────────────────────
 { id: 'recall', icon: '⚠', label: 'Recall Engine' },
 { id: 'transparency', icon: '💬', label: 'Transparency' },
 { id: 'dpp', icon: '📋', label: 'Digital Passports' },
 { id: 'iot', icon: '📡', label: 'IoT Cold Chain' },
 { id: 'portal', icon: '🏭', label: 'Supplier Portal' },
 // ── v7 ────────────────────────────────────────────────
 { id: 'users', icon: '👥', label: 'Team' },
 { id: 'billing', icon: '💳', label: 'Billing' },
 // ── v7 Phase 2 ────────────────────────────────────────
 { id: 'import', icon: '⬆', label: 'Bulk Import' },
 { id: 'webhooks', icon: '⚡', label: 'Webhooks' },
 { id: 'analytics', icon: '📈', label: 'Analytics' },
 { id: 'reports', icon: '📄', label: 'Reports' },
];

interface SidebarProps {
 active: string;
 onNav: (id: string) => void;
 alerts: Alert[];
 agents: AgentState[];
 user: { display_name: string; role: string; email: string } | null;
 onLogout: () => void;
}

export default function Sidebar({ active, onNav, alerts, agents, user, onLogout }: SidebarProps) {
 const unread = alerts.filter(a => a.status === 'open').length;
 const critAgents = agents.filter(a => a.status === 'alert' || a.status === 'error').length;

 return (
 <div style={{
 width: 220, minHeight: '100vh', background: '#0a0908',
 borderRight: `1px solid ${T.border}`,
 display: 'flex', flexDirection: 'column',
 position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
 }}>
 {/* Logo */}
 <div style={{ padding: '24px 20px 20px', borderBottom: `1px solid ${T.border}` }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
 <div style={{
 width: 36, height: 36, borderRadius: 10,
 background: `linear-gradient(135deg, ${T.copper}, #d4a847)`,
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 fontSize: 18, boxShadow: `0 0 20px ${T.copper}30`, flexShrink: 0,
 }}>◎</div>
 <div>
 <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 15, color: T.text, letterSpacing: -.3 }}>
 TraceFull
 </div>
 <div style={{ fontSize: 9, color: T.dim, letterSpacing: .6, textTransform: 'uppercase' }}>v6 Platform</div>
 </div>
 </div>
 </div>

 {/* Nav */}
 <nav style={{ padding: '12px 10px', flex: 1, overflowY: 'auto' }}>
 {NAV_ITEMS.map((item, idx) => {
 // Section divider before v6 features
 const isFirstV6 = item.id === 'recall';
 const isActive = active === item.id;
 const badge = item.id === 'alerts' ? unread : item.id === 'agents' ? critAgents : 0;

 return (
 <div key={item.id}>
 {isFirstV6 && (
 <div style={{ fontSize: 8, color: T.dim, textTransform: 'uppercase', letterSpacing: 1, padding: '10px 12px 5px', fontWeight: 700 }}>
 v6 — New
 </div>
 )}
 <button onClick={() => onNav(item.id)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10,
 padding: '9px 12px', borderRadius: 9, marginBottom: 2,
 background: isActive ? `${T.copper}18` : 'transparent',
 border: `1px solid ${isActive ? T.copper + '40' : 'transparent'}`,
 color: isActive ? T.copper : T.sub,
 cursor: 'pointer', textAlign: 'left', transition: 'all .12s',
 fontSize: 13, fontWeight: isActive ? 600 : 400,
 }}>
 <span style={{ fontSize: 14, flexShrink: 0, width: 18, textAlign: 'center' }}>{item.icon}</span>
 <span style={{ flex: 1 }}>{item.label}</span>
 {badge > 0 && (
 <span style={{
 minWidth: 18, height: 18, borderRadius: 9, fontSize: 9, fontWeight: 700,
 background: item.id === 'alerts' ? T.crimson : T.amber,
 color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
 padding: '0 5px',
 }}>
 {badge}
 </span>
 )}
 </button>
 </div>
 );
 })}
 </nav>

 {/* Active agents strip */}
 {agents.length > 0 && (
 <div style={{ padding: '12px 14px', borderTop: `1px solid ${T.border}` }}>
 <div style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 8 }}>Agents</div>
 <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
 {agents.map(a => (
 <div key={a.id} title={`${a.name}: ${a.current_task || a.status}`} style={{
 width: 8, height: 8, borderRadius: '50%',
 background: STATUS_COLOR[a.status] || T.sub,
 animation: a.status === 'active' || a.status === 'negotiating' ? 'pulse 2s infinite' : 'none',
 }} />
 ))}
 </div>
 </div>
 )}

 {/* User */}
 {user && (
 <div style={{ padding: '12px 14px', borderTop: `1px solid ${T.border}` }}>
 <div style={{ fontSize: 11, color: T.text, fontWeight: 600, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
 {user.display_name}
 </div>
 <div style={{ fontSize: 10, color: T.sub, marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
 {user.email}
 </div>
 <button onClick={onLogout} style={{
 background: 'transparent', border: `1px solid ${T.border}`,
 color: T.sub, borderRadius: 7, padding: '5px 10px',
 cursor: 'pointer', fontSize: 10, fontWeight: 500, width: '100%',
 }}>
 Sign out
 </button>
 </div>
 )}
 </div>
 );
}
